import { EventLightVm, MatchDetailVm } from '../../utils/api';
import { periodCountFromSegment, PeriodCount } from '../../helpers/matchTiming';
import { eventMapPeriodKey, MapPeriodKey, sortEventsChronological } from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';

/*
 * Game-state / clutch from light events. Score before each event is derived by walking
 * goals chronologically. "Last 5 minutes" = last regulation Period or OT/PS with countdown minute < 5.
 */

export type ScoreState = 'leading' | 'tied' | 'trailing';

export interface ShotStateBucket {
    shots: number;
    goals: number;
}

export interface GameStateSplits {
    leading: ShotStateBucket;
    tied: ShotStateBucket;
    trailing: ShotStateBucket;
    /** Final 5 min of Q4/OT/PS and margin ≤ 2 before the shot. */
    clutch: ShotStateBucket;
}

const emptyBucket = (): ShotStateBucket => ({ shots: 0, goals: 0 });

export const emptyGameStateSplits = (): GameStateSplits => ({
    leading: emptyBucket(),
    tied: emptyBucket(),
    trailing: emptyBucket(),
    clutch: emptyBucket(),
});

export const scoreStateFromDiff = (ourGoals: number, theirGoals: number): ScoreState => {
    if (ourGoals > theirGoals) return 'leading';
    if (ourGoals < theirGoals) return 'trailing';
    return 'tied';
};

/** Countdown clock: minute < 5 in last regulation Period / OT / PS ≈ last five minutes. */
const LAST_REGULATION_KEY: Record<PeriodCount, MapPeriodKey> = {
    1: 'q1',
    2: 'q2',
    3: 'q3',
    4: 'q4',
};

export const isLateClock = (event: EventLightVm, periodCount: PeriodCount = 4): boolean => {
    const key = eventMapPeriodKey(event);
    if (key !== LAST_REGULATION_KEY[periodCount] && key !== 'ot' && key !== 'ps') return false;
    return (event.minute ?? 99) < 5;
};

export const isClutchSituation = (
    event: EventLightVm,
    ourGoals: number,
    theirGoals: number,
    periodCount: PeriodCount = 4
): boolean => isLateClock(event, periodCount) && Math.abs(ourGoals - theirGoals) <= 2;

const bump = (bucket: ShotStateBucket, isGoal: boolean): void => {
    bucket.shots += 1;
    if (isGoal) bucket.goals += 1;
};

export const shootPct = (bucket: ShotStateBucket): number | undefined =>
    bucket.shots > 0 ? Math.round((bucket.goals / bucket.shots) * 100) : undefined;

/**
 * Our-team (or one player) shooting splits by score state.
 * If playerMatchPlayerIds is set, only shots taken by those match-player ids count.
 */
export const buildGameStateSplits = (
    bundle: MatchBundle,
    ourTeamIds: number[],
    playerMatchPlayerIds?: Set<number>
): GameStateSplits => {
    const result = emptyGameStateSplits();
    const { detail, events } = bundle;
    const periodCount = periodCountFromSegment(detail.matchSegmentId);
    let home = 0;
    let away = 0;

    sortEventsChronological(events).forEach((event: EventLightVm) => {
        if (!event.shot) return;

        const ourGoalsBefore = ourTeamIds.includes(detail.homeTeamId) ? home : away;
        const theirGoalsBefore = ourTeamIds.includes(detail.homeTeamId) ? away : home;
        const state = scoreStateFromDiff(ourGoalsBefore, theirGoalsBefore);

        const ours = ourTeamIds.includes(event.shot.teamId);
        if (ours) {
            const mpOk =
                playerMatchPlayerIds == null ||
                (event.shot.takenById != null && playerMatchPlayerIds.has(event.shot.takenById));
            if (mpOk) {
                bump(result[state], !!event.shot.isGoal);
                if (isClutchSituation(event, ourGoalsBefore, theirGoalsBefore, periodCount)) {
                    bump(result.clutch, !!event.shot.isGoal);
                }
            }
        }

        if (event.shot.isGoal) {
            if (event.shot.teamId === detail.homeTeamId) home += 1;
            else if (event.shot.teamId === detail.awayTeamId) away += 1;
        }
    });

    return result;
};

/** Aggregate game-state across many matches (player season). */
export const mergeGameStateSplits = (parts: GameStateSplits[]): GameStateSplits => {
    const result = emptyGameStateSplits();
    parts.forEach((part) => {
        (['leading', 'tied', 'trailing', 'clutch'] as const).forEach((key) => {
            result[key].shots += part[key].shots;
            result[key].goals += part[key].goals;
        });
    });
    return result;
};

export const matchPlayerIdsForPlayer = (
    detail: MatchDetailVm,
    playerId: number,
    ourTeamIds: number[]
): Set<number> => {
    const ids = new Set<number>();
    (detail.players || []).forEach((mp) => {
        if (mp.id == null || mp.playerId !== playerId) return;
        if (!ourTeamIds.includes(mp.teamId)) return;
        ids.add(mp.id);
    });
    return ids;
};
